import React from 'react';
import { useParams } from 'react-router-dom';
import { useBookings } from '../../../../hooks/accommodation';
import { useAppSelector } from '../../../../hooks/useRedux';
import { selectAccommodationProperty } from '../../../../store/accommodation';
import { BookingStatusBadge } from './BookingStatusBadge';

function Detail({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <span className="block text-xs text-gray-400 font-medium">{label}</span>
      <span className="text-sm font-semibold text-gray-900 mt-1 block">
        {value}
      </span>
    </div>
  );
}

function formatDate(iso: string) {
  return new Date(iso).toLocaleDateString(undefined, {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
}

function nightsBetween(checkIn: string, checkOut: string) {
  return Math.round(
    (new Date(checkOut).getTime() - new Date(checkIn).getTime()) / 86_400_000,
  );
}

export const BookingOverviewCard: React.FC = () => {
  const { bookingId } = useParams<{ bookingId: string }>();
  const property = useAppSelector(selectAccommodationProperty);
  const { data: bookings = [] } = useBookings(property?.id);
  const booking = bookings.find((b) => b.id === bookingId) ?? null;

  if (!booking) return null;

  const nights = nightsBetween(booking.check_in, booking.check_out);

  return (
    <div className="rounded-2xl border border-gray-200 bg-white p-5 space-y-4">
      <h4 className="text-xs font-semibold uppercase tracking-wide text-gray-500 flex items-center gap-1.5 pb-2 border-b border-gray-100">
        📅 Booking Overview
      </h4>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <span className="block text-xs text-gray-400 font-medium">
            Check-in
          </span>
          <span className="text-sm font-semibold text-gray-900 mt-1 block">
            {formatDate(booking.check_in)}
          </span>
        </div>
        <div>
          <span className="block text-xs text-gray-400 font-medium">
            Check-out
          </span>
          <span className="text-sm font-semibold text-gray-900 mt-1 block">
            {formatDate(booking.check_out)}
          </span>
        </div>
      </div>

      <div className="flex justify-between items-center bg-slate-50 rounded-xl px-4 py-2 text-xs font-semibold text-gray-700">
        <span>Total Nights</span>
        <span className="text-pos-primary bg-pos-primary/10 px-2.5 py-0.5 rounded-lg font-bold">
          {nights} night{nights !== 1 ? 's' : ''}
        </span>
      </div>

      <hr className="border-gray-100" />

      <div className="grid grid-cols-2 gap-4 text-xs">
        <Detail label="Adults" value={String(booking.adults)} />
        <Detail label="Children" value={String(booking.children)} />
      </div>

      <hr className="border-gray-100" />

      <div className="space-y-3 text-xs">
        <Detail
          label="Property"
          value={
            property
              ? `${property.name}${property.address ? `, ${property.address}` : ''}`
              : '—'
          }
        />

        <div className="flex justify-between items-center">
          <span className="text-xs text-gray-400 font-medium">Source</span>
          <span className="font-medium text-gray-900 inline-flex items-center gap-1.5 bg-slate-100 px-2.5 py-0.5 rounded-lg">
            {booking.booking_source?.name ||
              booking.source_name ||
              booking.source}
          </span>
        </div>

        <Detail
          label="Booking Ref"
          value={
            booking.external_id
              ? `${booking.external_id} / ID: ${booking.id.slice(0, 8).toUpperCase()}`
              : `ID: ${booking.id.slice(0, 8).toUpperCase()}`
          }
        />

        <div className="flex justify-between items-center">
          <span className="text-xs text-gray-400 font-medium">Status</span>
          <BookingStatusBadge status={booking.status} />
        </div>

        {booking.ota_collects_payment && (
          <div className="inline-flex items-center gap-1.5 rounded-full bg-amber-50 px-2.5 py-0.5 text-xs font-semibold text-amber-700 border border-amber-200">
            OTA Collects Payment
          </div>
        )}
      </div>

      {booking.notes && (
        <>
          <hr className="border-gray-100" />
          <div>
            <span className="block text-xs text-gray-400 font-medium mb-1.5">
              Notes
            </span>
            <p className="text-xs text-gray-700 bg-slate-50 rounded-xl p-3 leading-relaxed">
              {booking.notes}
            </p>
          </div>
        </>
      )}
    </div>
  );
};
